import { AccountStatusEnum } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  adjustTimeZoneOffsetWithUtc,
  parseDateAsUtc,
} from '@monetize/utils/core';
import { useQueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns/addDays';
import { addSeconds } from 'date-fns/addSeconds';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useRef, useState } from 'react';
import { useGeneratePastInvoice } from '../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../api/axios';
import { accountServiceQueryKeys } from '../../../../api/queryKeysService';
import { useACL } from '../../../../services/acl/acl';
import { useToast } from '../../../../services/toast';

const GEN_INVOICE_RETRIES = 3;
const GEN_INVOICE_INTERVAL = 1000 * 10; // 10 seconds

export const usePastInvoice = ({
  accountId,
  billGroupId,
  billGroupStatus,
  nextInvoiceDate,
  invoiceDelayDirty,
  invoiceDelay,
}: {
  accountId: string;
  billGroupId: string;
  billGroupStatus: string;
  nextInvoiceDate: string;
  invoiceDelayDirty?: Maybe<boolean>;
  invoiceDelay?: Maybe<number>;
}) => {
  const { canDo } = useACL();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [hasPastInvoices, setHasPastInvoices] = useState(() => false);

  useEffect(() => {
    if (!nextInvoiceDate) {
      setHasPastInvoices(false);
      return;
    }

    if (invoiceDelayDirty) {
      setHasPastInvoices(false);
      return;
    }

    const expectedInvoiceDate = addDays(
      parseDateAsUtc(nextInvoiceDate),
      invoiceDelay ?? 0,
    );

    const expectedInvoiceDateAtUtc = startOfDay(expectedInvoiceDate);
    const currentDateMinusOneSecondAtUtc = addSeconds(
      startOfDay(adjustTimeZoneOffsetWithUtc(new Date())),
      1,
    );

    const hasPastDueInvoices = isBefore(
      expectedInvoiceDateAtUtc,
      currentDateMinusOneSecondAtUtc,
    );
    setHasPastInvoices(hasPastDueInvoices);
  }, [invoiceDelay, invoiceDelayDirty, nextInvoiceDate]);

  const genInvoiceInterval = useRef<{
    timeout: any;
    remainingChecks: number;
  }>({ timeout: null, remainingChecks: 5 });

  const { mutate: generatePreviousInvoice, isPending: isLoading } =
    useGeneratePastInvoice({
      onSuccess: () => {
        addToast({
          summary: `Invoice Scheduled for Creation`,
          detail: `Creation may take a few minutes.  Clicking 'Generate' again may result in multiple invoices erroneously created.`,
          severity: 'success',
        });
        setHasPastInvoices(false);
        // Refetch the invoice {GEN_INVOICE_RETRIES} times, every {GEN_INVOICE_INTERVAL} seconds
        genInvoiceInterval.current.remainingChecks = GEN_INVOICE_RETRIES;
        genInvoiceInterval.current.timeout = setInterval(() => {
          queryClient.invalidateQueries({
            queryKey:
              accountServiceQueryKeys.billGroups.billGroupByAccount(accountId),
          });
          queryClient.invalidateQueries({
            queryKey:
              accountServiceQueryKeys.billGroups.billGroupById(billGroupId),
          });
          queryClient.invalidateQueries({
            queryKey:
              accountServiceQueryKeys.accounts.accountDetailOverview(accountId),
          });
          genInvoiceInterval.current.remainingChecks--;
          if (genInvoiceInterval.current.remainingChecks <= 0) {
            clearInterval(genInvoiceInterval.current.timeout);
          }
        }, GEN_INVOICE_INTERVAL);
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const canUpdate =
    canDo([['billing', 'update']]) &&
    billGroupStatus === AccountStatusEnum.ACTIVE;

  return {
    hasPastInvoices,
    canUpdate,
    isLoading,
    generatePreviousInvoice,
  };
};
